import { useRef, useState, useCallback, type ReactNode } from 'react';
import { GripVertical, Minus, X } from 'lucide-react';
import { useLayoutStore } from '../stores/layoutStore';
import { snapRect } from './panelLayoutEngine';
import { usePanelEngagementStore } from '../stores/panelEngagementStore';
import type { PanelId } from '../types';

interface DraggablePanelProps {
  id: PanelId;
  title: string;
  children: ReactNode;
  className?: string;
  headerClassName?: string;
  /** When true, panel is pinned in the chrome layer (not draggable). */
  docked?: boolean;
  /** Flush dock under NavSidebar — flat top, rounded bottom, no drag chrome. */
  sidebarDocked?: boolean;
  /** Extra screen-space offset (e.g. canvas pan when host is outside pan layer). */
  positionOffset?: { x: number; y: number };
}

export function DraggablePanel({
  id,
  title,
  children,
  className = '',
  headerClassName = '',
  docked = false,
  sidebarDocked = false,
  positionOffset,
}: DraggablePanelProps & { className?: string; headerClassName?: string }) {
  const {
    panels,
    movePanel,
    resizePanel,
    hidePanel,
    minimizePanel,
    maximizePanel,
    bringPanelToFront,
    snapGrid,
  } = useLayoutStore();
  const stackOrder = usePanelEngagementStore((s) => s.stackOrder);
  const [isResizing, setIsResizing] = useState(false);
  const [resizePreview, setResizePreview] = useState<{ w: number; h: number } | null>(null);
  const resizePreviewRef = useRef<{ w: number; h: number } | null>(null);
  const resizeStartRef = useRef({ x: 0, y: 0, w: 0, h: 0 });

  const layout = panels[id];

  const offsetX = positionOffset?.x ?? 0;
  const offsetY = positionOffset?.y ?? 0;
  const layoutX = layout?.x ?? 100;
  const layoutY = layout?.y ?? 100;
  const x = layoutX + offsetX;
  const y = layoutY + offsetY;
  const w = isResizing && resizePreview ? resizePreview.w : (layout?.w ?? 400);
  const h = layout?.minimized ? 44 : (layout?.h ?? 300);
  const minimized = layout?.minimized ?? false;
  const resizable = layout?.resizable ?? false;

  const handlePanelFocus = useCallback(() => {
    bringPanelToFront(id);
  }, [id, bringPanelToFront]);

  const stackRank = stackOrder.indexOf(id);
  const zIndex =
    (stackRank >= 0 ? 20 + stackRank : 20) + (minimized ? 0 : 10);

  // DRAG: Header drag handler
  const handleHeaderPointerDown = useCallback((e: React.PointerEvent) => {
    if (docked) return;
    if (e.button !== 0) return;
    handlePanelFocus();
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const panelX = layoutX;
    const panelY = layoutY;

    const handleMove = (e: PointerEvent) => {
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      movePanel(id, panelX + dx, panelY + dy);
    };

    const handleUp = () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);

      if (snapGrid) {
        const current = useLayoutStore.getState().panels[id];
        if (current) {
          const snapped = snapRect({
            x: current.x ?? panelX,
            y: current.y ?? panelY,
            w: current.w ?? 400,
            h: current.h ?? 300,
          });
          movePanel(id, snapped.x, snapped.y);
        }
      }
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
  }, [id, layoutX, layoutY, movePanel, docked, handlePanelFocus, snapGrid]);

  // RESIZE: corner/edge drag handler
  const handleResizePointerDown = useCallback((e: React.PointerEvent, direction: string) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      w: layout?.w ?? 400,
      h: layout?.h ?? 300,
    };
    resizePreviewRef.current = { w: layout?.w ?? 400, h: layout?.h ?? 300 };
    setResizePreview({ w: layout?.w ?? 400, h: layout?.h ?? 300 });

    const handleMove = (e: PointerEvent) => {
      const dx = e.clientX - resizeStartRef.current.x;
      const dy = e.clientY - resizeStartRef.current.y;
      let newW = resizeStartRef.current.w;
      let newH = resizeStartRef.current.h;
      if (direction.includes('e')) newW = Math.max((layout?.minW ?? 200), resizeStartRef.current.w + dx);
      if (direction.includes('s')) newH = Math.max((layout?.minH ?? 150), resizeStartRef.current.h + dy);
      if (direction.includes('se')) {
        newW = Math.max((layout?.minW ?? 200), resizeStartRef.current.w + dx);
        newH = Math.max((layout?.minH ?? 150), resizeStartRef.current.h + dy);
      }
      resizePreviewRef.current = { w: newW, h: newH };
      setResizePreview({ w: newW, h: newH });
    };

    const handleUp = () => {
      const preview = resizePreviewRef.current;
      if (preview) {
        if (snapGrid) {
          const snapped = snapRect({
            x: 0,
            y: 0,
            w: preview.w,
            h: preview.h,
          });
          resizePanel(id, snapped.w, snapped.h);
        } else {
          resizePanel(id, preview.w, preview.h);
        }
      }
      resizePreviewRef.current = null;
      setResizePreview(null);
      setIsResizing(false);
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
  }, [id, layout?.w, layout?.h, layout?.minW, layout?.minH, resizePanel, snapGrid]);

  if (!layout?.visible) return null;

  const shellRadius = sidebarDocked
    ? 'rounded-t-none rounded-b-xl border-t-0 -mt-px'
    : 'rounded-xl';

  return (
    <div
      className={`absolute bg-canvas-surface border border-canvas-border flex flex-col overflow-hidden pointer-events-auto ${shellRadius} ${
        sidebarDocked ? 'shadow-sm' : 'shadow-md'
      } ${className}`.trim()}
      style={{
        left: x,
        top: y,
        width: w,
        height: minimized ? 44 : (layout?.autoHeight ? 'auto' : (isResizing && resizePreview ? resizePreview.h : h)),
        zIndex: minimized ? zIndex - 5 : zIndex,
        minWidth: layout?.minW || 200,
        minHeight: minimized ? 44 : (layout?.minH || 150),
        transition: isResizing ? 'none' : 'box-shadow 0.15s ease',
      }}
      onPointerDown={handlePanelFocus}
    >
      {/* DRAG HEADER with grip, title, minimize, close */}
      {title && !sidebarDocked ? (
        <div
          className={`flex items-center justify-between px-3 py-2 border-b border-canvas-border select-none shrink-0 ${headerClassName}`}
          onPointerDown={handleHeaderPointerDown}
          style={{ cursor: docked ? 'default' : 'grab' }}
        >
          <div className="flex items-center gap-2">
            <GripVertical size={14} className="text-canvas-faint shrink-0" />
            <span className="text-sm font-medium text-canvas">{title}</span>
          </div>
          <div className="flex items-center gap-0.5">
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (minimized) {
                  maximizePanel(id);
                } else {
                  minimizePanel(id);
                }
              }}
              className="p-1.5 rounded-lg hover:bg-canvas-surface-subtle text-canvas-faint hover:text-canvas-muted transition-colors"
              style={{ cursor: 'pointer' }}
            >
              <Minus size={14} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); hidePanel(id); }}
              className="p-1.5 rounded-lg hover:bg-canvas-surface-subtle text-canvas-faint hover:text-canvas-muted transition-colors"
              style={{ cursor: 'pointer' }}
            >
              <X size={14} />
            </button>
          </div>
        </div>
      ) : title && sidebarDocked ? (
        <div
          className={`flex items-center justify-between px-2 py-1.5 border-b border-canvas-border select-none shrink-0 ${headerClassName}`}
        >
          <span className="text-[10px] font-medium text-canvas-muted truncate">
            {title}
          </span>
          <button
            onClick={(e) => { e.stopPropagation(); hidePanel(id); }}
            className="p-1 rounded-md hover:bg-canvas-surface-subtle text-canvas-faint hover:text-canvas-muted transition-colors shrink-0"
            style={{ cursor: 'pointer' }}
            aria-label="Close voice panel"
          >
            <X size={12} />
          </button>
        </div>
      ) : (
        /* Custom close button for panels without title */
        !minimized && (
          <div className="absolute top-0 right-0 z-30 p-1">
            <button
              onClick={(e) => { e.stopPropagation(); hidePanel(id); }}
              className="p-1.5 rounded-lg hover:bg-canvas-surface/10 text-canvas-faint hover:text-white transition-colors"
              style={{ cursor: 'pointer' }}
            >
              <X size={14} />
            </button>
          </div>
        )
      )}

      {/* CONTENT (hidden when minimized) */}
      {!minimized && (
        <div className="flex-1 overflow-hidden flex flex-col">
          {children}
        </div>
      )}

      {/* RESIZE HANDLES */}
      {resizable && !minimized && !layout.autoHeight && (
        <>
          <div
            className="absolute bottom-0 right-0 w-5 h-5 z-20"
            style={{ cursor: 'se-resize' }}
            onPointerDown={(e) => handleResizePointerDown(e, 'se')}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" className="absolute bottom-1 right-1 text-canvas-faint">
              <path d="M1 9L9 1M4 9L9 4" stroke="currentColor" strokeWidth="1.5" fill="none" />
            </svg>
          </div>
          <div
            className="absolute top-10 right-0 w-2 h-[calc(100%-40px)] z-20"
            style={{ cursor: 'e-resize' }}
            onPointerDown={(e) => handleResizePointerDown(e, 'e')}
          />
          <div
            className="absolute bottom-0 left-4 w-[calc(100%-40px)] h-2 z-20"
            style={{ cursor: 's-resize' }}
            onPointerDown={(e) => handleResizePointerDown(e, 's')}
          />
        </>
      )}
    </div>
  );
}
